import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { IonInput, IonItem, ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';

import {
  GroupManagementService,
  QuickAddParticipant,
} from '@services/group-management.service';
import { PanelHostService } from '@services/panel-host.service';
import { TripService } from '@services/trip.service';
import { AccountTripForUser } from '../../../models';
import { hasTripLeadership } from '../../../services/trip-access/trip-guards';
import { TripAccessService } from '../../../services/trip-access/trip-access.service';
import { TripInterestService } from '../../../services/trip-interest.service';
import { ButtonComponent } from '../../ui/button/button.component';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';
import { InterestedPartiesListComponent } from '../../interested-parties-list/interested-parties-list.component';

interface QuickAddFormValue {
  firstname: string;
  lastname: string;
  email: string;
}

@Component({
  selector: 'app-add-participant',
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    IonInput,
    IonItem,
    ButtonComponent,
    ContentContainerComponent,
    PanelBodyComponent,
    InterestedPartiesListComponent,
  ],
  templateUrl: './add-participant.component.html',
  styleUrls: ['./add-participant.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddParticipantComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripService = inject(TripService);
  private readonly tripAccess = inject(TripAccessService);
  private readonly tripInterest = inject(TripInterestService);
  private readonly groupManagementService = inject(GroupManagementService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);
  private readonly fb = inject(FormBuilder);
  private readonly injector = inject(Injector);
  private readonly firstNameInput = viewChild<IonInput>('firstNameInput');

  readonly quickAddForm: FormGroup = this.fb.group({
    firstname: ['', Validators.required],
    lastname: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
  });

  submitting = signal<boolean>(false);
  duplicateEmailError = signal<boolean>(false);

  /** Leads on a standard trip see people from the interest page who are not on the roster yet. */
  readonly showInterestedParties = computed(() => {
    const canManage =
      this.tripAccess.context(this.tripService.currentTrip.value())?.canManageInterestedParties ?? false;
    const parties = this.tripInterest.parties.hasValue() ? this.tripInterest.parties.value() : [];
    return canManage && parties.some((party) => party.convertedAt === null);
  });

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() === 'add-participant') {
        this.resetForm();
        untracked(() => this.tripInterest.parties.reload());
      }
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  openImport(): void {
    this.panelHost.open('participant-import');
  }

  submit(): void {
    this.duplicateEmailError.set(false);

    if (this.quickAddForm.invalid) {
      this.quickAddForm.markAllAsTouched();
      return;
    }

    const trip = this.tripService.currentTrip.value();
    if (!trip) {
      return;
    }

    const formValue = this.quickAddForm.getRawValue() as QuickAddFormValue;
    const participant: QuickAddParticipant = {
      firstname: formValue.firstname.trim(),
      lastname: formValue.lastname.trim(),
      email: formValue.email.trim(),
    };

    if (this.isDuplicateEmail(trip, participant.email)) {
      this.duplicateEmailError.set(true);
      return;
    }

    this.submitting.set(true);
    const username = `${participant.firstname} ${participant.lastname}`;

    this.groupManagementService
      .addParticipant(trip.id, participant)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              marker('group-management.add-participant.add-success'),
              { username },
            ),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
          this.resetForm();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              marker('group-management.add-participant.add-failed'),
              { username },
            ),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }

  emailErrorText(): string {
    const emailControl = this.quickAddForm.get('email');
    if (emailControl?.hasError('required')) {
      return this.transloco.translate(
        marker('group-management.add-participant.missing-fields'),
      );
    }
    if (emailControl?.hasError('email')) {
      return this.transloco.translate(
        marker('group-management.add-participant.invalid-email'),
      );
    }
    return '';
  }

  private resetForm(): void {
    this.quickAddForm.reset({
      firstname: '',
      lastname: '',
      email: '',
    });
    this.quickAddForm.markAsUntouched();
    this.duplicateEmailError.set(false);
    this.submitting.set(false);
    afterNextRender(() => {
      void this.firstNameInput()?.setFocus();
    }, { injector: this.injector });
  }

  private isDuplicateEmail(trip: AccountTripForUser, email: string): boolean {
    if (!hasTripLeadership(trip)) {
      return false;
    }

    const normalized = email.trim().toLowerCase();
    const roster = trip.roster;
    const nonRosterUsers = trip.nonRosterUsers;

    const onRoster = roster.some(
      (p) => (p.email ?? '').trim().toLowerCase() === normalized,
    );
    const onNonRoster = nonRosterUsers.some(
      (p) => (p.email ?? '').trim().toLowerCase() === normalized,
    );
    return onRoster || onNonRoster;
  }
}
